import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import Header from './Header';
import Footer from './Footer';
import './CertificationPage.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };
}

function progressPercent(completed, total) {
  if (!total) return 0;
  return Math.round((completed / total) * 100);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProgressRing({ pct, size = 60, stroke = 5 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="url(#certGrad)" strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <defs>
        <linearGradient id="certGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#e0a3ff" />
          <stop offset="100%" stopColor="#ff69b4" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function CourseCard({ course, onOpen, onDelete }) {
  const pct = progressPercent(course.completed_modules, course.total_modules);
  return (
    <div className="cert-course-card" onClick={() => onOpen(course.id)}>
      <div className="cert-course-card-header">
        <span className={`cert-level-badge cert-level-${course.level}`}>{course.level}</span>
      </div>
      <h3 className="cert-course-card-title">{course.course_name}</h3>
      <p className="cert-course-card-meta">{course.duration_value} {course.duration_unit} · {course.hours_per_day}h/day</p>
      <div className="cert-course-card-progress">
        <ProgressRing pct={pct} />
        <div className="cert-course-card-progress-text">
          <span className="cert-progress-pct">{pct}%</span>
          <span className="cert-progress-label">{course.completed_modules}/{course.total_modules} modules</span>
        </div>
      </div>
      {course.status === 'completed' && <div className="cert-completed-badge">✓ Completed</div>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CertificationPage() {
  const navigate = useNavigate();
  const { user } = useUser();

  // Top-level view
  const [view, setView] = useState('home'); // home | creating | roadmap

  // Courses list
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  // Active course/roadmap
  const [activeCourse, setActiveCourse] = useState(null);
  const [activeModules, setActiveModules] = useState([]);
  const [activeCert, setActiveCert] = useState(null);
  const [roadmapLoading, setRoadmapLoading] = useState(false);

  // Setup form
  const [form, setForm] = useState({
    courseName: '', level: 'beginner', durationValue: '4', durationUnit: 'weeks', hoursPerDay: '2',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Module popup
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleContent, setModuleContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  // Quiz modal
  const [quizPhase, setQuizPhase] = useState(null); // null | 'module' | 'final'
  const [quizData, setQuizData] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizSubmitting, setQuizSubmitting] = useState(false);

  // Certificate
  const [certView, setCertView] = useState(false);
  const [certData, setCertData] = useState(null);
  const certRef = useRef(null);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // ─── Auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!localStorage.getItem('token')) navigate('/');
  }, [navigate]);

  // ─── Fetch courses ────────────────────────────────────────────────────────────
  const fetchCourses = useCallback(async () => {
    setCoursesLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/certification/courses`, { headers: authHeaders() });
      const d = await r.json();
      if (r.ok) setCourses(d.courses || []);
    } catch (e) {
      console.error(e);
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  // ─── Open course roadmap ──────────────────────────────────────────────────────
  const openCourse = useCallback(async (courseId) => {
    setRoadmapLoading(true);
    setView('roadmap');
    try {
      const r = await fetch(`${API_BASE_URL}/api/certification/course/${courseId}`, { headers: authHeaders() });
      const d = await r.json();
      if (r.ok) {
        setActiveCourse(d.course);
        setActiveModules(d.modules || []);
        setActiveCert(d.certificate || null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setRoadmapLoading(false);
    }
  }, []);

  // ─── Create course ────────────────────────────────────────────────────────────
  const handleCreate = async e => {
    e.preventDefault();
    if (!form.courseName.trim()) { setCreateError('Please enter a course name'); return; }
    if (courses.length >= 3) {
      setCreateError('You have reached the maximum of 3 courses. Please delete an existing course to create a new one.');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/certification/create`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          courseName: form.courseName,
          level: form.level,
          durationValue: form.durationValue,
          durationUnit: form.durationUnit,
          hoursPerDay: form.hoursPerDay,
        }),
      });
      const d = await r.json();
      if (r.ok) {
        await fetchCourses();
        await openCourse(d.courseId);
      } else {
        setCreateError(d.message || 'Failed to create course');
        setCreating(false);
      }
    } catch (e) {
      setCreateError('Network error. Please try again.');
      setCreating(false);
    }
  };

  // ─── Open module popup ────────────────────────────────────────────────────────
  const openModule = async (mod) => {
    // Determine if unlocked
    const moduleIdx = activeModules.findIndex(m => m.id === mod.id);
    if (moduleIdx > 0) {
      const prev = activeModules[moduleIdx - 1];
      if (!prev.is_completed) return; // locked
    }

    setSelectedModule(mod);
    setModuleContent(null);
    setContentLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/certification/module/${mod.id}/content`, {
        method: 'POST', headers: authHeaders(),
      });
      const d = await r.json();
      if (r.ok) setModuleContent(d.content);
    } catch (e) {
      console.error(e);
    } finally {
      setContentLoading(false);
    }
  };

  // ─── Start module quiz ────────────────────────────────────────────────────────
  const startModuleQuiz = async () => {
    setQuizLoading(true);
    setQuizPhase('module');
    setQuizData(null);
    setQuizAnswers({});
    setQuizResult(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/certification/module/${selectedModule.id}/quiz`, {
        method: 'POST', headers: authHeaders(),
      });
      const d = await r.json();
      if (r.ok) setQuizData(d.quiz);
    } catch (e) {
      console.error(e);
    } finally {
      setQuizLoading(false);
    }
  };

  // ─── Submit module quiz ───────────────────────────────────────────────────────
  const submitModuleQuiz = async () => {
    if (!quizData || !selectedModule || !activeCourse) return;
    const answered = Object.keys(quizAnswers).length;
    if (answered < quizData.questions.length) {
      alert('Please answer all questions before submitting.');
      return;
    }
    setQuizSubmitting(true);
    let correct = 0;
    quizData.questions.forEach((q, i) => {
      if (quizAnswers[i] === q.correct) correct++;
    });
    const score = Math.round((correct / quizData.questions.length) * 100);
    const passed = score >= 70;

    try {
      await fetch(`${API_BASE_URL}/api/certification/module/${selectedModule.id}/complete`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ courseId: activeCourse.id, score }),
      });
      setQuizResult({ score, passed, correct, total: quizData.questions.length });
      if (passed) {
        // Refresh course data
        const r = await fetch(`${API_BASE_URL}/api/certification/course/${activeCourse.id}`, { headers: authHeaders() });
        const d = await r.json();
        if (r.ok) {
          setActiveCourse(d.course);
          setActiveModules(d.modules || []);
          setActiveCert(d.certificate || null);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setQuizSubmitting(false);
    }
  };

  // ─── Start final quiz ─────────────────────────────────────────────────────────
  const startFinalQuiz = async () => {
    setQuizLoading(true);
    setQuizPhase('final');
    setQuizData(null);
    setQuizAnswers({});
    setQuizResult(null);
    setSelectedModule(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/certification/course/${activeCourse.id}/final-quiz`, {
        method: 'POST', headers: authHeaders(),
      });
      const d = await r.json();
      if (r.ok) setQuizData(d.quiz);
    } catch (e) {
      console.error(e);
    } finally {
      setQuizLoading(false);
    }
  };

  // ─── Submit final quiz ────────────────────────────────────────────────────────
  const submitFinalQuiz = async () => {
    if (!quizData) return;
    const answered = Object.keys(quizAnswers).length;
    if (answered < quizData.questions.length) {
      alert('Please answer all questions before submitting.');
      return;
    }
    setQuizSubmitting(true);
    let correct = 0;
    quizData.questions.forEach((q, i) => {
      if (quizAnswers[i] === q.correct) correct++;
    });
    const score = Math.round((correct / quizData.questions.length) * 100);

    try {
      const r = await fetch(`${API_BASE_URL}/api/certification/course/${activeCourse.id}/final-submit`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ score }),
      });
      const d = await r.json();
      setQuizResult({ score, passed: d.passed, correct, total: quizData.questions.length, certificate: d.certificate });
      if (d.passed && d.certificate) {
        setActiveCert(d.certificate);
        // Refresh course
        const r2 = await fetch(`${API_BASE_URL}/api/certification/course/${activeCourse.id}`, { headers: authHeaders() });
        const d2 = await r2.json();
        if (r2.ok) { setActiveCourse(d2.course); setActiveModules(d2.modules || []); }
        await fetchCourses();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setQuizSubmitting(false);
    }
  };

  // ─── View certificate ─────────────────────────────────────────────────────────
  const viewCertificate = () => {
    if (!activeCert || !activeCourse) return;
    setCertData({
      ...activeCert,
      user_name: user?.name || user?.email || 'Learner',
      course_name: activeCourse.roadmap?.title || activeCourse.course_name,
      level: activeCourse.level,
    });
    setCertView(true);
  };

  // ─── Delete course ────────────────────────────────────────────────────────────
  const deleteCourse = async (courseId) => {
    try {
      await fetch(`${API_BASE_URL}/api/certification/course/${courseId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      await fetchCourses();
      if (activeCourse?.id === courseId) {
        setView('home');
        setActiveCourse(null);
        setActiveModules([]);
      }
    } catch (e) {
      console.error(e);
    }
    setDeleteConfirm(null);
  };

  // ─── Close quiz / module ──────────────────────────────────────────────────────
  const closeQuiz = () => {
    const wasModule = quizPhase === 'module';
    setQuizPhase(null);
    setQuizData(null);
    setQuizAnswers({});
    setQuizResult(null);
    // Always clear the module so the popup doesn't reopen after quiz closes
    if (wasModule) {
      setSelectedModule(null);
      setModuleContent(null);
    }
  };

  const closeModule = () => {
    setSelectedModule(null);
    setModuleContent(null);
  };

  // ─── Module helpers ───────────────────────────────────────────────────────────
  const getModuleStatus = (mod, idx) => {
    if (mod.is_completed) return 'completed';
    if (idx === 0) return 'available';
    const prev = activeModules[idx - 1];
    return prev?.is_completed ? 'available' : 'locked';
  };

  const allModulesDone = activeCourse && activeCourse.completed_modules >= activeCourse.total_modules;

  // ─── Group modules by week ────────────────────────────────────────────────────
  const modulesByWeek = {};
  activeModules.forEach((mod, idx) => {
    const wk = mod.week_number;
    if (!modulesByWeek[wk]) modulesByWeek[wk] = [];
    modulesByWeek[wk].push({ ...mod, _idx: idx });
  });

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="cert-page">
      <Header />
      <main className="cert-main">

        {/* ── HOME ── */}
        {view === 'home' && (
          <div className="cert-home">
            {(() => {
              const atLimit = courses.length >= 3;
              return (
                <div className="cert-home-hero">
                  <div className="cert-hero-badge">🚀 AI-Powered</div>
                  <h1 className="cert-hero-title">AI <span className="cert-gradient-text">Certification Courses</span></h1>
                  <p className="cert-hero-sub">Generate a personalized learning roadmap, master each topic day by day, and earn a verified certificate.</p>
                  {atLimit ? (
                    <div className="cert-one-at-a-time">
                      ⚠️ You've reached the maximum of 3 courses. Delete an existing course to create a new one.
                    </div>
                  ) : (
                    <button className="cert-btn-primary cert-btn-large" onClick={() => setView('creating')}>
                      + Create New Course {courses.length > 0 && `(${courses.length}/3)`}
                    </button>
                  )}
                </div>
              );
            })()}

            {coursesLoading ? (
              <div className="cert-spinner-wrap"><div className="cert-spinner" /></div>
            ) : courses.length === 0 ? (
              <div className="cert-empty">
                <div className="cert-empty-icon">🎓</div>
                <p>No courses yet. Create your first personalized learning roadmap!</p>
              </div>
            ) : (
              <div className="cert-courses-grid">
                {courses.map(c => (
                  <CourseCard
                    key={c.id}
                    course={c}
                    onOpen={openCourse}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── CREATE FORM ── */}
        {view === 'creating' && (
          <div className="cert-create-wrap">
            <div className="cert-create-card">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
                <button
                  className="cert-back-btn"
                  onClick={() => { setView('home'); setCreateError(''); setCreating(false); }}
                  style={{ marginRight: 'auto' }}
                >
                  ← My Courses
                </button>
              </div>
              <h2 className="cert-create-title">Design Your Learning Path</h2>
              <p className="cert-create-sub">Tell us what you want to learn and we'll build a detailed day-by-day roadmap.</p>

              {creating ? (
                <div className="cert-generating">
                  <div className="cert-spinner cert-spinner-large" />
                  <h3>Building your roadmap…</h3>
                  <p>AI is crafting a personalized curriculum for you. This takes about 30 seconds.</p>
                </div>
              ) : (
                <form className="cert-form" onSubmit={handleCreate}>
                  <div className="cert-form-group">
                    <label>What do you want to learn?</label>
                    <input
                      className="cert-input"
                      type="text"
                      placeholder="e.g. SQL, React, Machine Learning, Python, AWS..."
                      value={form.courseName}
                      onChange={e => setForm(f => ({ ...f, courseName: e.target.value }))}
                    />
                  </div>

                  <div className="cert-form-row">
                    <div className="cert-form-group">
                      <label>Course Level</label>
                      <select className="cert-select" value={form.level} onChange={e => setForm(f => ({ ...f, level: e.target.value }))}>
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                    <div className="cert-form-group">
                      <label>Study Hours Per Day</label>
                      <select className="cert-select" value={form.hoursPerDay} onChange={e => setForm(f => ({ ...f, hoursPerDay: e.target.value }))}>
                        <option value="1">1 hour</option>
                        <option value="2">2 hours</option>
                        <option value="3">3 hours</option>
                        <option value="4">4 hours</option>
                        <option value="5">5 hours</option>
                        <option value="6">6+ hours</option>
                      </select>
                    </div>
                  </div>

                  <div className="cert-form-row">
                    <div className="cert-form-group">
                      <label>Duration</label>
                      <input
                        className="cert-input"
                        type="number"
                        min="1"
                        max="52"
                        value={form.durationValue}
                        onChange={e => setForm(f => ({ ...f, durationValue: e.target.value }))}
                      />
                    </div>
                    <div className="cert-form-group">
                      <label>Unit</label>
                      <select className="cert-select" value={form.durationUnit} onChange={e => setForm(f => ({ ...f, durationUnit: e.target.value }))}>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                  </div>

                  {createError && <p className="cert-error">{createError}</p>}

                  <button type="submit" className="cert-btn-primary cert-btn-full">
                    Generate My Roadmap →
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* ── ROADMAP ── */}
        {view === 'roadmap' && (
          <div className="cert-roadmap-view">
            {roadmapLoading ? (
              <div className="cert-spinner-wrap"><div className="cert-spinner" /></div>
            ) : activeCourse && (
              <>
                {/* Course header */}
                <div className="cert-course-header">
                  <div className="cert-course-header-text">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <button className="cert-back-btn" onClick={() => { setView('home'); setActiveCourse(null); }}>← My Courses</button>
                      {activeCert && (
                        <button className="cert-btn-certificate" onClick={viewCertificate}>🏆 View Certificate</button>
                      )}
                    </div>
                    <span className={`cert-level-badge cert-level-${activeCourse.level}`}>{activeCourse.level}</span>
                    <h2 className="cert-course-title">{activeCourse.roadmap?.title || activeCourse.course_name}</h2>
                    <p className="cert-course-desc">{activeCourse.roadmap?.description}</p>
                  </div>
                  <div className="cert-course-stats">
                    <div className="cert-stat">
                      <span className="cert-stat-num">{activeCourse.total_modules}</span>
                      <span className="cert-stat-label">Modules</span>
                    </div>
                    <div className="cert-stat">
                      <span className="cert-stat-num">{activeCourse.completed_modules}</span>
                      <span className="cert-stat-label">Done</span>
                    </div>
                    <div className="cert-stat">
                      <ProgressRing pct={progressPercent(activeCourse.completed_modules, activeCourse.total_modules)} size={70} stroke={6} />
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="cert-progress-bar-wrap">
                  <div className="cert-progress-bar">
                    <div
                      className="cert-progress-bar-fill"
                      style={{ width: progressPercent(activeCourse.completed_modules, activeCourse.total_modules) + '%' }}
                    />
                  </div>
                  <span className="cert-progress-bar-pct">{progressPercent(activeCourse.completed_modules, activeCourse.total_modules)}% complete</span>
                </div>

                {/* Roadmap weeks */}
                <div className="cert-roadmap">
                  {Object.entries(modulesByWeek).map(([weekNum, mods]) => {
                    const weekTheme = activeCourse.roadmap?.weeks?.find(w => w.weekNumber === parseInt(weekNum))?.theme;
                    return (
                      <div key={weekNum} className="cert-week">
                        <div className="cert-week-header">
                          <span className="cert-week-label">Week {weekNum}</span>
                          {weekTheme && <span className="cert-week-theme">{weekTheme}</span>}
                        </div>
                        <div className="cert-week-modules">
                          {mods.map((mod, i) => {
                            const status = getModuleStatus(mod, mod._idx);
                            const topics = (() => { try { return JSON.parse(mod.topics_json || '[]'); } catch { return []; } })();
                            return (
                              <div
                                key={mod.id}
                                className={`cert-module-card cert-module-${status}`}
                                onClick={() => status !== 'locked' && openModule(mod)}
                              >
                                <div className="cert-module-day">Day {mod.day_number}</div>
                                <div className="cert-module-status-icon">
                                  {status === 'completed' ? '✓' : status === 'locked' ? '🔒' : '▶'}
                                </div>
                                <h4 className="cert-module-title">{mod.title}</h4>
                                <p className="cert-module-desc">{mod.description}</p>
                                <div className="cert-module-topics">
                                  {topics.slice(0, 3).map((t, ti) => (
                                    <span key={ti} className="cert-topic-chip">{t}</span>
                                  ))}
                                </div>
                                {mod.quiz_score != null && (
                                  <div className="cert-module-score">Quiz: {mod.quiz_score}%</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Final exam button */}
                {allModulesDone && !activeCert && (
                  <div className="cert-final-wrap">
                    <div className="cert-final-card">
                      <div className="cert-final-icon">🎯</div>
                      <h3>You've completed all modules!</h3>
                      <p>Take the final certification exam (20 questions, 70% to pass) to earn your certificate.</p>
                      <button className="cert-btn-primary cert-btn-large" onClick={startFinalQuiz}>
                        Start Final Exam →
                      </button>
                    </div>
                  </div>
                )}

                {activeCert && (
                  <div className="cert-final-wrap">
                    <div className="cert-final-card cert-final-card-success">
                      <div className="cert-final-icon">🏆</div>
                      <h3>Certificate Earned!</h3>
                      <p>Congratulations! You've successfully completed this course.</p>
                      <button className="cert-btn-primary cert-btn-large" onClick={viewCertificate}>
                        View My Certificate →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </main>

      {/* ── MODULE POPUP ── */}
      {selectedModule && quizPhase === null && (
        <div className="cert-overlay" onClick={closeModule}>
          <div className="cert-module-popup" onClick={e => e.stopPropagation()}>
            <button className="cert-popup-close" onClick={closeModule}>×</button>
            <div className="cert-popup-scroll">
              <div className="cert-popup-header">
                <span className="cert-popup-day">Day {selectedModule.day_number} · Week {selectedModule.week_number}</span>
                <h2 className="cert-popup-title">{selectedModule.title}</h2>
                {selectedModule.is_completed && <span className="cert-popup-done-badge">✓ Completed</span>}
              </div>

              {contentLoading ? (
                <div className="cert-spinner-wrap"><div className="cert-spinner" /></div>
              ) : moduleContent ? (
                <div className="cert-popup-content">
                  <p className="cert-popup-overview">{moduleContent.overview}</p>

                  <div className="cert-popup-section">
                    <h3>Key Points</h3>
                    <ul className="cert-key-points">
                      {moduleContent.keyPoints?.map((pt, i) => <li key={i}>{pt}</li>)}
                    </ul>
                  </div>

                  {moduleContent.sections?.map((sec, i) => (
                    <div key={i} className="cert-popup-section">
                      <h3>{sec.heading}</h3>
                      <p>{sec.content}</p>
                    </div>
                  ))}

                  <div className="cert-popup-section cert-exercise-section">
                    <h3>✏️ Practical Exercise</h3>
                    <p>{moduleContent.practicalExercise}</p>
                  </div>

                  {moduleContent.youtubeSearchQueries?.length > 0 && (
                    <div className="cert-popup-section">
                      <h3>📺 Watch & Learn</h3>
                      <div className="cert-youtube-btns">
                        {moduleContent.youtubeSearchQueries.map((q, i) => (
                          <a
                            key={i}
                            href={`https://www.youtube.com/results?search_query=${encodeURIComponent(q)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cert-youtube-btn"
                          >
                            ▶ {q}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="cert-popup-section">
                    <h3>📌 Key Takeaways</h3>
                    <ul className="cert-key-points">
                      {moduleContent.keyTakeaways?.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </div>

                  <div className="cert-popup-actions">
                    {!selectedModule.is_completed ? (
                      <button className="cert-btn-primary cert-btn-full" onClick={startModuleQuiz}>
                        Mark as Finished → Take Quiz
                      </button>
                    ) : (
                      <div className="cert-popup-completed-msg">
                        ✓ You have already passed this module's quiz.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="cert-error">Failed to load module content. Please try again.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── QUIZ MODAL ── */}
      {quizPhase !== null && (
        <div className="cert-overlay" onClick={quizResult ? undefined : undefined}>
          <div className="cert-quiz-modal" onClick={e => e.stopPropagation()}>
            <div className="cert-quiz-header">
              <h2>{quizPhase === 'final' ? '🎯 Final Certification Exam' : '📝 Module Quiz'}</h2>
              {quizPhase === 'module' && <span>{selectedModule?.title}</span>}
              {!quizResult && <button className="cert-popup-close" onClick={closeQuiz}>×</button>}
            </div>

            <div className="cert-quiz-body">
              {quizLoading ? (
                <div className="cert-spinner-wrap"><div className="cert-spinner" /></div>
              ) : quizResult ? (
                <QuizResult
                  result={quizResult}
                  phase={quizPhase}
                  onClose={closeQuiz}
                  onRetry={() => { setQuizResult(null); setQuizAnswers({}); }}
                  onViewCert={() => { closeQuiz(); viewCertificate(); }}
                />
              ) : quizData ? (
                <QuizQuestions
                  questions={quizData.questions}
                  answers={quizAnswers}
                  onChange={(i, v) => setQuizAnswers(a => ({ ...a, [i]: v }))}
                  onSubmit={quizPhase === 'final' ? submitFinalQuiz : submitModuleQuiz}
                  submitting={quizSubmitting}
                />
              ) : (
                <p className="cert-error">Failed to load quiz. Please close and try again.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── CERTIFICATE MODAL ── */}
      {certView && (
        <div className="cert-overlay" onClick={() => setCertView(false)}>
          <div className="cert-cert-modal" onClick={e => e.stopPropagation()}>
            <button className="cert-popup-close" onClick={() => setCertView(false)}>×</button>
            {certData ? (
              <Certificate data={certData} certRef={certRef} />
            ) : (
              <div className="cert-spinner-wrap"><div className="cert-spinner" /></div>
            )}
          </div>
        </div>
      )}


      <Footer />
    </div>
  );
}

// ─── Quiz Questions Sub-component ──────────────────────────────────────────────

function QuizQuestions({ questions, answers, onChange, onSubmit, submitting }) {
  return (
    <div className="cert-quiz-questions">
      {questions.map((q, i) => (
        <div key={i} className="cert-quiz-q">
          <p className="cert-quiz-q-text"><span className="cert-quiz-qnum">Q{i + 1}.</span> {q.question}</p>
          <div className="cert-quiz-options">
            {q.options.map((opt, j) => (
              <label key={j} className={`cert-quiz-option ${answers[i] === j ? 'cert-quiz-option-selected' : ''}`}>
                <input type="radio" name={`q${i}`} value={j} checked={answers[i] === j} onChange={() => onChange(i, j)} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      <div className="cert-quiz-footer">
        <span className="cert-quiz-answered">{Object.keys(answers).length}/{questions.length} answered</span>
        <button
          className="cert-btn-primary"
          onClick={onSubmit}
          disabled={submitting || Object.keys(answers).length < questions.length}
        >
          {submitting ? 'Submitting…' : 'Submit Answers'}
        </button>
      </div>
    </div>
  );
}

// ─── Quiz Result Sub-component ─────────────────────────────────────────────────

function QuizResult({ result, phase, onClose, onRetry, onViewCert }) {
  return (
    <div className="cert-quiz-result">
      <div className={`cert-result-icon ${result.passed ? 'cert-result-pass' : 'cert-result-fail'}`}>
        {result.passed ? '🎉' : '😔'}
      </div>
      <h3 className={result.passed ? 'cert-result-pass-text' : 'cert-result-fail-text'}>
        {result.passed ? 'Passed!' : 'Not quite — try again!'}
      </h3>
      <div className="cert-result-score">{result.score}%</div>
      <p className="cert-result-detail">{result.correct} out of {result.total} correct · Need 70% to pass</p>
      {phase === 'final' && result.passed && (
        <p className="cert-result-cert-msg">🏆 Your certificate has been issued!</p>
      )}
      <div className="cert-result-actions">
        {!result.passed && phase === 'final' && <button className="cert-btn-secondary" onClick={onRetry}>Retry Exam</button>}
        {phase === 'final' && result.passed ? (
          <button className="cert-btn-primary" onClick={onViewCert}>View Certificate →</button>
        ) : (
          <button className={result.passed ? 'cert-btn-primary' : 'cert-btn-secondary'} onClick={onClose}>
            {result.passed ? 'Continue →' : phase === 'module' ? 'Close · Re-open module to try again' : 'Close'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Certificate Sub-component ─────────────────────────────────────────────────

function Certificate({ data, certRef }) {
  const [downloading, setDownloading] = useState(false);

  const date = new Date(data.issued_at).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const handleDownload = async () => {
    if (!certRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(certRef.current, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [canvas.width / 3, canvas.height / 3],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 3, canvas.height / 3);
      pdf.save(`${data.course_name} - Certificate.pdf`);
    } catch (e) {
      console.error('PDF generation failed:', e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="cert-certificate-wrap">
      <div className="cert-certificate" ref={certRef}>
        <div className="cert-cert-border">
          <div className="cert-cert-header">
            <div className="cert-cert-logo">
              <img src="/logo192.png" alt="Terview" className="cert-cert-logo-img" />
              <span className="cert-cert-logo-text">TERVIEW</span>
            </div>
            <div className="cert-cert-title">Certificate of Completion</div>
          </div>
          <p className="cert-cert-presented">This is to certify that</p>
          <h2 className="cert-cert-name">{data.user_name}</h2>
          <p className="cert-cert-completed">has successfully completed the course</p>
          <h3 className="cert-cert-course">"{data.course_name}"</h3>
          <p className="cert-cert-level">{data.level.charAt(0).toUpperCase() + data.level.slice(1)} Level</p>
          <div className="cert-cert-footer">
            <div className="cert-cert-date">{date}</div>
            <div className="cert-cert-id">Certificate ID: {data.certificate_id}</div>
          </div>
          <div className="cert-cert-seal">✦</div>
        </div>
      </div>
      <button
        className="cert-btn-primary cert-btn-large"
        style={{ marginTop: 20 }}
        onClick={handleDownload}
        disabled={downloading}
      >
        {downloading ? 'Generating PDF…' : '⬇️ Download as PDF'}
      </button>
    </div>
  );
}
